/**
 * GameLoop - Centralized game tick timing manager
 *
 * Provides single source of truth for game tick timing with:
 * - RAF (requestAnimationFrame) for active tabs
 * - Fallback setInterval timer for backgrounded tabs
 * - Subscriber pattern for tick callbacks with priority ordering
 *
 * Part of Phase 6 timer consolidation - replaces scattered timer logic
 * with centralized management.
 *
 * Usage:
 *   import { GameLoop, getGameLoop } from './systems/GameLoop.js';
 *
 *   // Create instance
 *   const gameLoop = new GameLoop();
 *
 *   // Or use singleton pattern
 *   const gameLoop = getGameLoop();
 *
 *   // Subscribe to tick callbacks
 *   gameLoop.subscribe(onTick, GameLoop.Priority.NORMAL);
 *
 *   // Start the loop
 *   gameLoop.start();
 */

import { TIMING } from '../config/constants.js';

// =============================================================================
// PRIORITY CONSTANTS
// =============================================================================

/**
 * Priority levels for tick callbacks
 * Lower values run first (HIGH runs before NORMAL runs before LOW)
 */
export const Priority = Object.freeze({
    HIGH: 0,
    NORMAL: 1,
    LOW: 2
});

// =============================================================================
// GAMELOOP CLASS
// =============================================================================

export class GameLoop {
    /**
     * Static reference to priority constants
     */
    static Priority = Priority;

    constructor() {
        /** @type {Array<{callback: Function, priority: number}>} */
        this.subscribers = [];

        /** @type {boolean} */
        this.isRunning = false;

        /** @type {boolean} */
        this.isPaused = false;

        /** @type {number|null} */
        this.rafId = null;

        /** @type {number|null} */
        this.fallbackTimerId = null;

        /** @type {boolean} */
        this.isUsingFallback = false;

        /** @type {number} */
        this.lastTimestamp = 0;

        // Bind methods to preserve context
        this._tick = this._tick.bind(this);
        this._fallbackTick = this._fallbackTick.bind(this);
        this._onVisibilityChange = this._onVisibilityChange.bind(this);
    }

    /**
     * Subscribe a callback to receive tick updates
     * @param {Function} callback - Callback function receiving deltaTime in seconds
     * @param {number} [priority=Priority.NORMAL] - Priority level (lower runs first)
     * @returns {void}
     */
    subscribe(callback, priority = Priority.NORMAL) {
        if (typeof callback !== 'function') {
            console.warn('[GameLoop] subscribe requires a function callback');
            return;
        }

        // Check if already subscribed
        const existing = this.subscribers.find(sub => sub.callback === callback);
        if (existing) {
            console.warn('[GameLoop] Callback already subscribed');
            return;
        }

        this.subscribers.push({ callback, priority });

        // Sort by priority (lower numbers first)
        this.subscribers.sort((a, b) => a.priority - b.priority);
    }

    /**
     * Unsubscribe a callback from tick updates
     * @param {Function} callback - The callback to remove
     * @returns {boolean} True if callback was found and removed
     */
    unsubscribe(callback) {
        const index = this.subscribers.findIndex(sub => sub.callback === callback);
        if (index !== -1) {
            this.subscribers.splice(index, 1);
            return true;
        }
        return false;
    }

    /**
     * Start the game loop
     * Registers visibility change listener and starts RAF loop
     */
    start() {
        if (this.isRunning) {
            console.warn('[GameLoop] Already running');
            return;
        }

        this.isRunning = true;
        this.isPaused = false;
        this.lastTimestamp = performance.now();

        // Listen for visibility changes to switch between RAF and fallback
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this._onVisibilityChange);
        }

        // Start appropriate loop based on visibility
        if (typeof document !== 'undefined' && document.hidden) {
            this._startFallbackTimer();
        } else {
            this._startRAFLoop();
        }

        console.log('[GameLoop] Started');
    }

    /**
     * Stop the game loop
     * Stops all timers and removes listeners
     */
    stop() {
        if (!this.isRunning) {
            return;
        }

        this.isRunning = false;

        // Stop RAF loop
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }

        // Stop fallback timer
        this._stopFallbackTimer();

        // Remove visibility listener
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this._onVisibilityChange);
        }

        console.log('[GameLoop] Stopped');
    }

    /**
     * Pause the game loop (keeps running but doesn't call subscribers)
     */
    pause() {
        this.isPaused = true;
    }

    /**
     * Resume the game loop from paused state
     */
    resume() {
        if (this.isPaused) {
            this.isPaused = false;
            // Reset timestamp to prevent large delta after unpause
            this.lastTimestamp = performance.now();
        }
    }

    /**
     * Dispose of the game loop
     * Stops the loop and clears all subscribers
     */
    dispose() {
        this.stop();
        this.subscribers = [];
        console.log('[GameLoop] Disposed');
    }

    /**
     * Get subscriber count (for debugging/testing)
     * @returns {number}
     */
    getSubscriberCount() {
        return this.subscribers.length;
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================

    /**
     * RAF tick handler
     * @param {number} timestamp - Performance timestamp from RAF
     * @private
     */
    _tick(timestamp) {
        if (!this.isRunning) return;

        // Calculate delta time in seconds
        const deltaMs = timestamp - this.lastTimestamp;
        this.lastTimestamp = timestamp;

        // Clamp delta to prevent huge jumps (e.g., after tab restore)
        const deltaSeconds = Math.min(deltaMs / 1000, 0.1);

        // Call subscribers if not paused
        if (!this.isPaused) {
            this._notifySubscribers(deltaSeconds);
        }

        // Schedule next frame
        this.rafId = requestAnimationFrame(this._tick);
    }

    /**
     * Fallback timer tick handler
     * @private
     */
    _fallbackTick() {
        if (!this.isRunning) return;

        const now = performance.now();
        const deltaMs = now - this.lastTimestamp;
        this.lastTimestamp = now;

        // Clamp delta
        const deltaSeconds = Math.min(deltaMs / 1000, 0.1);

        // Call subscribers if not paused
        if (!this.isPaused) {
            this._notifySubscribers(deltaSeconds);
        }
    }

    /**
     * Notify all subscribers with deltaTime
     * @param {number} deltaSeconds - Time since last tick in seconds
     * @private
     */
    _notifySubscribers(deltaSeconds) {
        for (const subscriber of this.subscribers) {
            try {
                subscriber.callback(deltaSeconds);
            } catch (error) {
                console.error('[GameLoop] Subscriber error:', error);
            }
        }
    }

    /**
     * Handle visibility change to switch between RAF and fallback
     * @private
     */
    _onVisibilityChange() {
        if (!this.isRunning) return;

        if (document.hidden) {
            // Tab hidden - switch to fallback timer
            this._stopRAFLoop();
            this._startFallbackTimer();
        } else {
            // Tab visible - switch to RAF
            this._stopFallbackTimer();
            this._startRAFLoop();
        }
    }

    /**
     * Start the RAF loop
     * @private
     */
    _startRAFLoop() {
        if (this.rafId !== null) return;

        this.isUsingFallback = false;
        this.lastTimestamp = performance.now();
        this.rafId = requestAnimationFrame(this._tick);
        console.log('[GameLoop] Using requestAnimationFrame');
    }

    /**
     * Stop the RAF loop
     * @private
     */
    _stopRAFLoop() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    /**
     * Start the fallback timer for backgrounded tabs
     * @private
     */
    _startFallbackTimer() {
        if (this.fallbackTimerId !== null) return;

        this.isUsingFallback = true;
        this.lastTimestamp = performance.now();

        // Calculate interval from TIMING.FALLBACK_FPS
        const intervalMs = 1000 / TIMING.FALLBACK_FPS;

        this.fallbackTimerId = setInterval(this._fallbackTick, intervalMs);
        console.log('[GameLoop] Using fallback timer for backgrounded tab');
    }

    /**
     * Stop the fallback timer
     * @private
     */
    _stopFallbackTimer() {
        if (this.fallbackTimerId !== null) {
            clearInterval(this.fallbackTimerId);
            this.fallbackTimerId = null;
            this.isUsingFallback = false;
        }
    }
}

// =============================================================================
// SINGLETON PATTERN
// =============================================================================

let _instance = null;

/**
 * Get or create GameLoop singleton
 * @returns {GameLoop} GameLoop instance
 */
export function getGameLoop() {
    if (!_instance) {
        _instance = new GameLoop();
    }
    return _instance;
}

/**
 * Reset singleton instance (for testing)
 */
export function resetGameLoop() {
    if (_instance) {
        _instance.dispose();
    }
    _instance = null;
}

// Export singleton instance for convenience
export const gameLoop = getGameLoop();
